export default {
  app: {
    title: 'Clips',
    description: 'Short-form clips for social media',
  },
  title: 'Clips',
  header: {
    title: 'Your',
    titleAccent: 'clips',
    subtitle: 'No project | 1 clip project | {total} clip projects',
    hint: 'Turn your videos into short clips ready for TikTok, Reels and Shorts.',
    newClip: 'New clip',
  },
  empty: {
    title: 'Create your first clip',
    description:
      'Pick a video from your library: the analysis spots its highlights and prepares a first cut, ready to adjust and export for TikTok, Reels or Shorts.',
    goToLibrary: 'Open my library',
  },
  create: {
    title: 'Create a',
    titleAccent: 'clip',
    pickVideo: 'Pick a video',
    sourceLabel: 'Source video',
    changeSource: 'Change',
    description: 'A draft is prepared automatically from the video highlights.',
    titleLabel: 'Clip title',
    formatLabel: 'Format',
    durationLabel: 'Target length',
    durations: {
      '30s': '30 sec',
      '1min': '1 min',
      '2min': '2 min',
    },
    accountOnly: 'Account',
    durationHint: 'The draft is assembled to fit that length. You can then add or remove sequences in the editor.',
    analysisNote:
      'The analysis relies on the transcript: hooks, complete statements, audience peaks. You can adjust every scene in the editor.',
    cancel: 'Cancel',
    confirm: 'Create the clip',
    created: 'Clip created — analysis running',
    error: 'Could not create the clip',
    action: 'Create a clip',
  },
  status: {
    analyzing: 'Analyzing…',
    ready: 'Draft',
    exporting: 'Exporting…',
    exported: 'Exported',
    failed: 'Error',
  },
  list: {
    filterLabel: 'Filter by state',
    filterAll: 'All',
    filterEmpty: 'No clip in this state.',
    scenes: 'No scene | 1 scene | {count} scenes',
    openEditor: 'Open editor',
    deleteProject: 'Delete',
    deleteConfirm: {
      title: 'Delete the clip',
      message: 'This clip project and all its scenes will be permanently deleted.',
      confirm: 'Delete the clip',
    },
    deleted: 'Project deleted',
    deleteError: 'Could not delete the clip',
    loading: 'Loading clip projects',
    loadError: 'The clip projects could not be loaded.',
    retry: 'Retry',
  },
  editor: {
    back: 'Clips',
    source: 'From "{name}"',
    rename: 'Rename the clip',
    mobile: {
      scenes: 'Scenes',
      settings: 'Settings',
    },
    preview: 'Preview',
    more: 'More actions',
    formatLabel: 'Format',
    export: 'Export',
    exportProgress: 'Exporting {progress}%',
    exportStarted: "Export started — you'll get an email and a notification as soon as your clip is ready.",
    exportDock: {
      toastProgress: '"{name}" — exporting {progress}%',
      toastReady: '"{name}" is ready 🎬',
      toastDownload: 'Download',
      toastFailed: 'The export of "{name}" failed',
      errorNetwork: 'Connection lost while tracking the export',
      errorGeneric: 'Export failed',
    },
    exportError: 'Export failed',
    exportUnsaved: 'Your latest changes could not be saved: fix them before exporting.',
    unsavedOnLeave: 'Your latest changes to the clip were not saved.',
    exportModal: {
      title: 'Clip exported 🎬',
      download: 'Download locally',
      downloadHint: 'MP4 file on your device',
      save: 'Save to the media library',
      saveHint: 'New video in your library',
      saved: 'Added to the library',
      open: 'Open the video',
      savedToast: 'Clip added to the media library',
      saveError: 'Could not save the clip to the library',
      reExport: 'Re-export',
      reExportHint: 'The export does not include changes made after it was started.',
      history: 'Previous exports',
      historyError: 'Could not download this export',
    },
    sourceChanged: {
      text: 'The original video has changed since this cut was made: your scenes may no longer fall in the right place. Check them, then keep this cut, or run the analysis again, which will replace your scenes and transcribe the video again if needed.',
      pastEnd: 'Scene {scenes} runs past the end of the new version: shorten it to keep this cut. | Scenes {scenes} run past the end of the new version: shorten them to keep this cut.',
      keep: 'Keep this cut',
      keepError: 'The cut could not be kept',
      reanalyze: 'Run the analysis again',
      exportBlocked: 'The original video has changed: choose what to do with this cut first.',
    },
    viewExport: 'View export',
    analyzing: {
      title: 'Analyzing the video…',
      description: 'We spot the highlights from the transcript. This page refreshes automatically.',
      stepCount: 'Step {current} / {total}',
      background: 'You can leave this page: the analysis keeps running in the background, and an email will tell you when it is done.',
      remaining: {
        underMinute: 'Less than a minute left',
        about: 'About {minutes} min left',
        range: 'About {low} to {high} min left',
        hours: 'About {low} to {high} left',
        queue: 'Not counting the wait if many videos are being processed',
      },
      steps: {
        transcribing: 'Transcribing the video',
        scoring: 'Spotting the highlights',
        ranking: 'Ranking the best moments',
        assembling: 'Assembling the draft',
        framing: 'Automatic framing',
      },
      hints: {
        transcribing: 'Speech becomes timed text, word by word',
        scoring: 'Hooks, complete statements, speech pace',
        ranking: 'AI scoring and real audience peaks',
        assembling: 'Scenes are assembled within the chosen length',
        framing: 'The detected face is tracked in every scene',
      },
    },
    analysisError: {
      title: 'The analysis failed',
      retry: 'Retry',
      retryError: 'The analysis could not be restarted',
      reasons: {
        transcription_failed: "The video's transcription did not complete. Retry to start a new one.",
        transcript_empty: 'The transcript holds no speech: there is nothing to cut from this video.',
        no_moment_fits: 'No part of the video fits the chosen length. Try a shorter one.',
        queue_full: 'Too much work is waiting for your organization. Retry in a few minutes.',
        interrupted: 'The analysis was interrupted. Retry to run it again.',
        analysis_failed: 'The analysis could not complete. Retry, and contact support if it fails again.',
      },
    },
    saveStatus: {
      saved: 'Saved',
      saving: 'Saving…',
      unsaved: 'Unsaved changes',
      error: 'Save error',
    },
    formats: {
      '9:16': '9:16',
      '4:5': '4:5',
      '1:1': '1:1',
    },
    scenes: {
      title: 'Clip scenes',
      count: '{count} · {duration}',
      targetHint: 'Target length picked at creation: {duration}',
      addScene: 'Add a scene',
      delete: 'Delete',
      start: 'Start',
      end: 'End',
      startAria: 'Scene start (m:ss)',
      endAria: 'Scene end (m:ss)',
      nudgeBack: 'One second earlier',
      nudgeForward: 'One second later',
      transcriptLabel: 'Scene transcript',
      transcriptPlaceholder: 'Scene transcript (editable)…',
      cleanFillers: 'Clean filler words',
      cleaned: 'No fillers',
      fillerCount: '1 filler | {count} fillers',
      maximum: 'A clip lasts at most 15 minutes: shorten your scenes to save.',
      cannotDeleteLast: 'A clip must keep at least one scene',
      reorderError: 'Could not reorder the scenes',
    },
    focus: {
      auto: 'Automatic',
      autoHint: 'Frames the detected face',
      autoShort: 'Auto',
      center: 'Centered',
      centerHint: 'Fixed frame at the center',
      manual: 'Manual',
      manualHint: 'Drag the frame on the video',
      cropLabel: {
        auto: '✦ Auto follow',
        center: 'Centered',
        manual: '✋ Manual',
      },
    },
    canvas: {
      noPreview: 'Source preview coming soon — the crop frame stays adjustable',
    },
    phone: {
      preview: 'Preview',
      play: 'Play the scene',
      pause: 'Pause',
    },
    inspector: {
      crop: 'Reframe',
      subtitles: 'Captions',
      branding: 'Branding',
      focusOfScene: 'Scene {number} focus',
      zoom: 'Zoom',
      removeSilences: 'Cut silences',
      removeSilencesHint: 'Removes long pauses between words',
      removeSilencesSaved: '−{duration} of pauses removed',
      showCaptions: 'Show captions',
      showCaptionsHint: 'Burned into the exported video',
      captionsOffWarning: '80% of social media videos are watched WITHOUT sound — captions are strongly recommended.',
      captionStyle: 'Caption style',
      keywordColor: 'Keyword color',
      keywordColorHint: 'Applies to the highlighted keywords of the captions.',
      position: 'Position',
      positions: { top: 'Top', mid: 'Middle', bot: 'Bottom' },
      textSize: 'Text size',
      options: 'Options',
      keywordsColored: 'Colored keywords',
      keywordsColoredHint: 'Automatically detected',
      hookText: 'On-screen hook',
      showHook: 'Show the hook',
      showHookHint: 'At the top of the clip',
      hookStyle: 'Hook style',
      hookPosition: 'Hook position on screen',
      hookStyles: {
        bar: 'Banner',
        bubble: 'Bubble',
        outline: 'Outline',
        minimal: 'Minimal',
      },
      logo: 'Logo',
      showLogo: 'Logo watermark',
      showLogoHint: 'Top right corner',
      logoUpload: 'Your logo',
      logoUploadHint: 'Transparent PNG recommended',
      progressBar: 'Progress bar',
      showProgressBar: 'Show the bar',
      showProgressBarHint: 'At the top of the clip',
      progressColor: 'Bar color',
      progressPosition: 'Bar position',
      presets: {
        karaoke: 'Karaoke',
        punch: 'Punch',
        minimal: 'Minimal',
        neon: 'Neon',
      },
    },
    timeline: {
      scene: 'Scene {number}',
      play: 'Play the clip',
      pause: 'Pause',
      scrub: 'Clip position',
    },
    previewModal: {
      close: 'Close the preview',
    },
    addScene: {
      title: 'Add a',
      titleAccent: 'scene',
      description: 'Pick an unused highlight, or set a passage manually.',
      suggested: 'Suggested moments',
      manual: 'Manual selection',
      noSuggestions: 'Every suggestion has been used — switch to manual selection.',
      startLabel: 'Start',
      endLabel: 'End',
      rangeAria: 'Passage selection — drag the window or its handles',
      initialFocus: 'Initial scene focus',
      cancel: 'Cancel',
      confirm: 'Add the scene',
      added: 'Scene added',
      invalidRange: 'End must be after start',
      tooLong: 'This scene would take the clip past the 15 minutes it can last.',
      tooMany: 'A clip holds at most 15 scenes: remove one to add another.',
    },
    scoreReasons: {
      question: 'Question',
      notable_number: 'Notable number',
      strong_hook: 'Strong hook',
      complete_statement: 'Complete statement',
      good_length: 'Good length',
      good_pace: 'Good pace',
      audience_peak: 'Audience peak',
    },
  },
}
